import dayjs from 'dayjs';
import { Form, Upload } from 'components';
import PostalMime from 'postal-mime';
import {
  useAttachmentFileBulkCreateMutation,
  useContactPersonReadQuery,
  useSupplierOfferByIdQuery,
  useUpdateSupplierOfferMutation,
  useUploadEmailMutation,
} from 'api';
import { ADMIN_ROUTES } from 'constants/routes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TypeOfEntityType } from 'models/ComunicationHub';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { CLIENT_EMAIL_ATTACHMENT_TYPE, EMAIL_ATTACHMENT_TYPE, MAX_EML_FILE_SIZE, MAX_FILE_SIZE } from 'constants/index';
import { SUPPLIER_OFFER_SOURCE, SupplierOfferSourceValueType } from 'models/SupplierOffer';

const validAttachmentFormats = ['image/jpeg', 'image/png', 'text/csv', 'application/pdf'];

export const useEdit = () => {
  const [form] = Form.useForm();
  const [recordId, setRecordId] = useState<string | undefined>();
  const [removeBrandId, setRemoveBrandId] = useState<string | undefined>();
  const [removeBrandModalState, setRemoveBrandModalState] = useState<boolean>(false);
  const [addBrandModalState, setAddBrandModalState] = useState<boolean>(false);
  const [emailFile, setEmailFile] = useState<File | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [attachmentTypes, setAttachmentTypes] = useState<string[]>([]);
  const [publishModalState, setPublishModalState] = useState<boolean>(false);
  const [openContactPersonModal, setOpenContactPersonModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<string | undefined>();
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<{ type: string; file: any }[]>([]);
  const [emailPreview, setEmailPreview] = useState<{
    dateTime?: string;
    subject?: string;
    from?: string;
    body?: string;
  } | null>(null);

  const navigate = useNavigate();
  const { handleError, handleWarning } = useNotificationMessages();
  const { supplierOfferId, source, entityId } = useParams() as {
    supplierOfferId: string;
    source: SupplierOfferSourceValueType;
    entityId: string;
  };
  const { data: supplierOfferDetails = {}, isLoading: supplierOfferIsLoading } = useSupplierOfferByIdQuery({
    supplierOfferId,
  });
  const [uploadEmail] = useUploadEmailMutation();
  const [createBulkFiles] = useAttachmentFileBulkCreateMutation();
  const [updateSupplierOffer] = useUpdateSupplierOfferMutation();
  const { data: contactPersons = [], isLoading: isLoadingContactPerson } = useContactPersonReadQuery(
    { entityId: selectedSupplier || '' },
    { skip: !selectedSupplier }
  );
  const onDraft = useCallback(() => {
    switch (source) {
      case SUPPLIER_OFFER_SOURCE.PO_ADD:
        navigate(ADMIN_ROUTES.purchaseOrder.create);
        break;
      case SUPPLIER_OFFER_SOURCE.PO_DETAILS:
        navigate(`${ADMIN_ROUTES.purchaseOrder.view}/${entityId}/general`);
        break;
      case SUPPLIER_OFFER_SOURCE.MAIN:
        navigate(ADMIN_ROUTES.supplierOffer.list);
        break;
    }
  }, [navigate, source, entityId]);

  const onPublish = useCallback((recordId?: string) => {
    setPublishModalState(true);
    setRecordId(recordId);
  }, []);
  const onPublishModalClose = useCallback(() => {
    setPublishModalState(false);
    setRecordId(undefined);
  }, []);
  const onAddBrandModalStateChange = useCallback(() => {
    setAddBrandModalState((oState) => !oState);
  }, []);
  const onRemoveBrandModalStateChange = useCallback((recordId?: string) => {
    setRemoveBrandId(recordId);
    setRemoveBrandModalState((oState) => !oState);
  }, []);

  const onFinish = useCallback(
    async (value?: Record<string, any>, overrideAttachments?: { type: string; file: any }[]) => {
      try {
        const payload = await form.validateFields();
        const usedAttachments = overrideAttachments || attachments;
        const { data, error }: any = await updateSupplierOffer({
          id: supplierOfferId,
          ...payload,
          ...value,
          deletedAttachmentIds: [...(value?.deletedAttachmentIds || []), ...deletedAttachmentIds],
          attachmentTypes: [...(value?.attachmentTypes || []), ...attachmentTypes],
        });

        if (data?.success) {
          if (usedAttachments.length && Array.isArray(data.data) && data.data.length) {
            const form = new FormData();
            form.append('attachmentIds', data.data.join(','));
            usedAttachments.forEach((att) => form.append('files', att.file));
            form.append('serviceName', 'WRD');
            await createBulkFiles({ data: form, entityName: 'PURCHASE_REQUEST' });
          }
          setDeletedAttachmentIds([]);
          setAttachmentTypes([]);
          //handleSuccess(`Supplier Offer was successfully updated.`);
        } else {
          handleError(error);
        }
      } catch (err) {
        console.warn(err);
      }
    },
    [
      updateSupplierOffer,
      form,
      handleError,
      supplierOfferId,
      createBulkFiles,
      attachments,
      deletedAttachmentIds,
      attachmentTypes,
    ]
  );

  const handleFormItemBlur = useCallback(
    (fieldKey: string, value: string | number, groupObj?: string) => {
      if (groupObj) {
        onFinish({
          [groupObj]: {
            [fieldKey]: value,
          },
        });
      } else {
        onFinish({ [fieldKey]: value });
      }
    },
    [onFinish]
  );
  const handleFormItemChange = useCallback(
    (fieldKey: string, value: string, groupObj?: string) => {
      if (groupObj) {
        onFinish({
          [groupObj]: {
            [fieldKey]: value,
          },
        });
      } else {
        if (fieldKey === 'supplierOfferValidity') {
          const originalDate = dayjs(value);
          const twoDaysBefore = originalDate.subtract(2, 'day');
          onFinish({ [fieldKey]: value, clientOfferValidity: twoDaysBefore.toISOString() });
        } else {
          onFinish({ [fieldKey]: value });
        }
      }
    },
    [onFinish]
  );

  const onAttachmentAdd = useCallback(
    (attachments: { type: string; file: File }[]) => {
      onFinish(
        {
          attachmentTypes: attachments.map((i) => i.type),
        },
        attachments
      );
      setAttachmentTypes((prevState) => [...prevState, ...attachments.map((i) => i.type)]);
    },
    [onFinish]
  );
  const onAttachmentDelete = useCallback(
    (ids: string[]) => {
      onFinish({
        deletedAttachmentIds: ids,
      });
      setDeletedAttachmentIds((prevState) => [...prevState, ...ids]);
    },
    [onFinish]
  );

  const handleFormChange = useCallback(
    (values: Record<string, any>) => {
      if (values.supplierId) {
        setSelectedSupplier(values.supplierId);
        form.resetFields(['contactPersonId']);
      }
    },
    [form]
  );

  const onRedirectToEntity = useCallback(
    (id: string, entityName: TypeOfEntityType) => {
      switch (entityName) {
        case 'BRAND':
          navigate(`${ADMIN_ROUTES.brand.view}/${id}/management`);
          return;
        case 'CLIENT':
          navigate(`${ADMIN_ROUTES.client}/${id}/general`);
          return;
        case 'LEAD':
          navigate(`${ADMIN_ROUTES.leads.view}/${id}/general`);
          return;
        case 'DEAL':
          navigate(`${ADMIN_ROUTES.deals.view}/${id}/purchase-request`);
          return;
        default:
          return;
      }
    },
    [navigate]
  );
  const handleEmailUpload = useCallback(
    async (file: File) => {
      if (file.size > MAX_EML_FILE_SIZE) {
        form.setFieldValue('emailFile', null);
        setEmailPreview(null);
        form.setFields([
          {
            name: 'emailFile',
            errors: [`File exceeds the maximum size of 20 MB.`],
          },
        ]);
        setAttachments((oState) => oState.filter((i) => i.type !== CLIENT_EMAIL_ATTACHMENT_TYPE));
        handleError('File exceeds the maximum size of 20 MB.');
        return Upload.LIST_IGNORE;
      }

      const arrayBuffer = await file.arrayBuffer();
      const parser = new PostalMime();
      const parsedEmail = await parser.parse(arrayBuffer);
      let newAttachments: any[] = [];
      let deleteAttachments: any[] = [];

      const cidMap: Record<string, string> = {};
      parsedEmail.attachments?.forEach((att: any) => {
        if (att.contentId && att.disposition === 'inline') {
          const blob = new Blob([att.content], { type: att.mimeType });
          const blobUrl = URL.createObjectURL(blob);
          const cid = att.contentId.replace(/^<|>$/g, '');
          cidMap[cid] = blobUrl;
        }
      });

      let emailHtml = parsedEmail.html || `<pre>${parsedEmail.text}</pre>`;
      for (const [cid, url] of Object.entries(cidMap)) {
        const regex = new RegExp(`cid:${cid}`, 'g');
        emailHtml = emailHtml.replace(regex, url);
      }

      const files: File[] = parsedEmail.attachments
        .map(
          (att) =>
            new File([att.content], att.filename || 'attachment', {
              type: att.mimeType || 'application/octet-stream',
            })
        )
        .filter((file) => validAttachmentFormats.includes(file.type) && file.size <= MAX_FILE_SIZE);

      if (parsedEmail.attachments.length > files.length) {
        handleWarning('Attachments that exceed 5 GB or are in an unsupported file format are not added.');
      }
      if (files.length) {
        setAttachments((oState) => {
          deleteAttachments = oState
            .filter((i) => i.type === CLIENT_EMAIL_ATTACHMENT_TYPE || i.type === EMAIL_ATTACHMENT_TYPE)
            .map((i: any) => i.id);
          newAttachments = [
            ...oState.filter((i) => i.type !== CLIENT_EMAIL_ATTACHMENT_TYPE && i.type !== EMAIL_ATTACHMENT_TYPE),
            ...files.map((file) => ({ type: CLIENT_EMAIL_ATTACHMENT_TYPE, file: file })),
          ];
          if (file) {
            newAttachments.push({ type: EMAIL_ATTACHMENT_TYPE, file: file });
          }
          return newAttachments;
        });
      } else {
        setAttachments((oState) => {
          deleteAttachments = oState
            .filter((i) => i.type === CLIENT_EMAIL_ATTACHMENT_TYPE || i.type === EMAIL_ATTACHMENT_TYPE)
            .map((i: any) => i.id);
          newAttachments = [
            ...oState.filter((i) => i.type !== CLIENT_EMAIL_ATTACHMENT_TYPE && i.type !== EMAIL_ATTACHMENT_TYPE),
          ];
          if (file) {
            newAttachments.push({ type: EMAIL_ATTACHMENT_TYPE, file: file });
          }
          return newAttachments;
        });
      }

      setEmailPreview({
        subject: parsedEmail.subject,
        from: parsedEmail.from.address,
        dateTime: parsedEmail.date,
        body: emailHtml,
      });

      setEmailFile(file);
      form.setFieldValue('emailFile', [file]); // ✅ this fixes the error

      // ✅ Upload the email and call onFinish here once everything is ready
      const emailFormData = new FormData();
      emailFormData.append('file', file);

      const emailResp = await uploadEmail(emailFormData);
      if (emailResp?.error) {
        handleError(emailResp.error);
        return Upload.LIST_IGNORE;
      }
      const emailId = emailResp?.data?.data;
      await onFinish(
        {
          emailId,
          attachmentTypes: [...files.map(() => CLIENT_EMAIL_ATTACHMENT_TYPE), EMAIL_ATTACHMENT_TYPE],
          deletedAttachmentIds: deleteAttachments,
        },
        [...newAttachments]
      );
      return false; // prevent default upload
    },
    [handleWarning, handleError, form, uploadEmail, onFinish]
  );

  useEffect(() => {
    if (supplierOfferDetails.supplierOffer) {
      form.setFieldsValue({
        ...supplierOfferDetails.supplierOffer,
        ...supplierOfferDetails.supplierOffer.fee,
        ...supplierOfferDetails.supplierOffer.shippingInformation,
        emailFile: supplierOfferDetails.email,
        clientOfferValidity: supplierOfferDetails.supplierOffer.clientOfferValidity
          ? dayjs(supplierOfferDetails.supplierOffer.clientOfferValidity)
          : null,
        supplierOfferValidity: supplierOfferDetails.supplierOffer.supplierOfferValidity
          ? dayjs(supplierOfferDetails.supplierOffer.supplierOfferValidity)
          : null,
      });
      setSelectedSupplier(supplierOfferDetails.supplierOffer.supplierId);
    }
    if (supplierOfferDetails.email) {
      setEmailPreview({
        subject: supplierOfferDetails.email.subject,
        from: supplierOfferDetails.email.fromEmail,
        dateTime: supplierOfferDetails.email.receivedDate,
        body: supplierOfferDetails.email.mailBodyHTML?.replace(/<a\s+(?![^>]*target=)/g, '<a target="_blank" '),
      });
    }
    if (supplierOfferDetails.attachments) {
      const formattedAttachments = supplierOfferDetails.attachments.map((attachment: any) => {
        return {
          ...attachment,
          type: attachment.attachmentType,
          file: {
            name: attachment.fileName,
          },
        };
      });
      setAttachments(formattedAttachments);
    }
  }, [
    supplierOfferDetails.supplierOffer,
    supplierOfferDetails.email,
    supplierOfferDetails.supllierOffer?.fee,
    supplierOfferDetails.supllierOffer?.shippingInformation,
    supplierOfferDetails.attachments,
    form,
  ]);
  const itemList = useMemo(() => {
    return Object.values(supplierOfferDetails.supplierOfferItems || {}).flat() || [];
  }, [supplierOfferDetails.supplierOfferItems]);

  return {
    form,
    emailFile,
    attachments,
    setAttachments,
    emailPreview,
    contactPersons,
    handleFormItemBlur,
    handleFormItemChange,
    selectedSupplier,
    handleFormChange,
    handleEmailUpload,
    onRedirectToEntity,
    isLoadingContactPerson,
    supplierOfferId,
    onAttachmentAdd,
    onAttachmentDelete,
    openContactPersonModal,
    setOpenContactPersonModal,
    onDraft,
    itemList,
    globalShippingCost: supplierOfferDetails.supplierOffer?.shippingInformation?.shippingCoast,
    leadType: supplierOfferDetails.supplierOffer?.shippingInformation?.leadTime,
    setVisibleColumns,
    visibleColumns,
    supplierOffer: supplierOfferDetails.supplierOffer || {},
    onPublish,
    recordId,
    publishModalState,
    supplierOfferItems: supplierOfferDetails.supplierOfferItems || {},
    onAddBrandModalStateChange,
    addBrandModalState,
    removeBrandModalState,
    onRemoveBrandModalStateChange,
    removeBrandId,
    source,
    entityId,
    supplierOfferLoadingState: supplierOfferIsLoading,
    onPublishModalClose,
  };
};
